import { useState, FormEvent, ChangeEvent } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

interface FormData {
  full_name: string;
  email: string;
  phone: string;
  company: string;
  industry: string;
  problem: string;
}

const industryOptions = [
  'Inteligência Artificial',
  'Desenvolvimento de Software',
  'Consultoria Tecnológica',
  'Análise de Dados & BI',
  'Automação de Processos',
  'Cloud & Infraestrutura',
  'Outro',
];

const contactInfo = [
  { icon: 'ri-mail-line', label: 'E-mail', value: 'contato@almadata.com.br' },
  { icon: 'ri-phone-line', label: 'Telefone', value: '(61) 99223-2205' },
  { icon: 'ri-map-pin-line', label: 'Localização', value: 'São Roque, SP — Brasil' },
  { icon: 'ri-time-line', label: 'Horário', value: 'Seg–Sex: 9h às 18h' },
];

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';

export default function ContactSection() {
  const { ref, visible } = useScrollAnimation();
  const [formData, setFormData] = useState<FormData>({
    full_name: '', email: '', phone: '', company: '', industry: '', problem: '',
  });
  const [charCount, setCharCount] = useState(0);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'problem') setCharCount(value.length);
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!formData.full_name.trim()) newErrors.full_name = 'Nome obrigatório';
    if (!formData.email.trim()) newErrors.email = 'E-mail obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'E-mail inválido';
    if (!formData.problem.trim()) newErrors.problem = 'Mensagem obrigatória';
    if (formData.problem.length > 500) newErrors.problem = 'Máximo de 500 caracteres';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('sending');
    try {
      const payload = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        industry: formData.industry,
        problem: formData.problem,
      };

      const res = await fetch(`${API_BASE_URL}/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setStatus('success');
        setFormData({ full_name: '', email: '', phone: '', company: '', industry: '', problem: '' });
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div ref={ref} className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block text-emerald-600 font-semibold text-sm font-inter tracking-widest uppercase mb-3">Vamos começar</span>
          <h2 className="font-sora font-bold text-3xl md:text-4xl text-zinc-900 mb-4">Fale com a Almadata</h2>
          <p className="font-inter text-zinc-500 max-w-xl mx-auto text-base">
            Conte-nos sobre o seu negócio e seus desafios. Nossa equipe irá analisar e retornar com uma proposta personalizada.
          </p>
          <div className="w-12 h-1 bg-emerald-500 rounded-full mx-auto mt-5" />
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-5 gap-10 transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Form */}
          <div className="lg:col-span-3 bg-white rounded-2xl border border-zinc-100 p-6 md:p-8">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-5">
                  <i className="ri-check-line text-emerald-600 text-3xl" />
                </div>
                <h3 className="font-sora font-bold text-xl text-zinc-900 mb-3">Mensagem enviada!</h3>
                <p className="font-inter text-zinc-500 text-sm mb-6 max-w-xs">
                  Obrigado pelo contato. Nossa equipe retornará em até 1 dia útil.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="text-emerald-600 font-semibold text-sm font-sora cursor-pointer hover:text-emerald-700 transition-colors"
                >
                  Enviar outra mensagem
                </button>
              </div>
            ) : (
              <form id="almadata-contact-form" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block font-inter text-xs font-medium text-zinc-700 mb-1.5">Nome *</label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      placeholder="Seu nome completo"
                      className={`w-full border rounded-lg px-3.5 py-3 text-sm font-inter text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-colors ${errors.full_name ? 'border-red-400' : 'border-zinc-200'}`}
                    />
                    {errors.full_name && <p className="text-red-500 text-xs mt-1 font-inter">{errors.full_name}</p>}
                  </div>
                  <div>
                    <label className="block font-inter text-xs font-medium text-zinc-700 mb-1.5">E-mail *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                      className={`w-full border rounded-lg px-3.5 py-3 text-sm font-inter text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-colors ${errors.email ? 'border-red-400' : 'border-zinc-200'}`}
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1 font-inter">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block font-inter text-xs font-medium text-zinc-700 mb-1.5">Telefone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(61) 99223-2205"
                      className="w-full border border-zinc-200 rounded-lg px-3.5 py-3 text-sm font-inter text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-inter text-xs font-medium text-zinc-700 mb-1.5">Empresa</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Nome da sua empresa"
                      className="w-full border border-zinc-200 rounded-lg px-3.5 py-3 text-sm font-inter text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block font-inter text-xs font-medium text-zinc-700 mb-1.5">Serviço de Interesse</label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    className="w-full border border-zinc-200 rounded-lg px-3.5 py-3 text-sm font-inter text-zinc-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Selecione um serviço</option>
                    {industryOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block font-inter text-xs font-medium text-zinc-700 mb-1.5">
                    Mensagem * <span className={`ml-1 ${charCount > 480 ? 'text-red-500' : 'text-zinc-400'}`}>({charCount}/500)</span>
                  </label>
                  <textarea
                    name="problem"
                    value={formData.problem}
                    onChange={handleChange}
                    rows={5}
                    maxLength={500}
                    placeholder="Conte um pouco sobre seu negócio e o que você precisa..."
                    className={`w-full border rounded-lg px-3.5 py-3 text-sm font-inter text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-colors resize-none ${errors.problem ? 'border-red-400' : 'border-zinc-200'}`}
                  />
                  {errors.problem && <p className="text-red-500 text-xs mt-1 font-inter">{errors.problem}</p>}
                </div>

                {status === 'error' && (
                  <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                    <i className="ri-error-warning-line text-red-500" />
                    <p className="font-inter text-red-600 text-sm">Erro ao enviar. Tente novamente ou nos chame pelo WhatsApp.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white py-3.5 rounded-full font-semibold font-sora text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {status === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      Enviar Mensagem
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            {contactInfo.map((c) => (
              <div key={c.label} className="flex items-start gap-4 bg-white rounded-xl border border-zinc-100 p-5">
                <div className="w-11 h-11 flex items-center justify-center bg-emerald-50 rounded-xl shrink-0">
                  <i className={`${c.icon} text-emerald-600 text-lg`} />
                </div>
                <div>
                  <p className="font-sora font-semibold text-zinc-900 text-sm mb-0.5">{c.label}</p>
                  <p className="font-inter text-zinc-500 text-sm">{c.value}</p>
                </div>
              </div>
            ))}

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/5561992232205?text=Ol%C3%A1%2C%20vim%20pelo%20site%20da%20Almadata%20e%20gostaria%20de%20conversar."
              target="_blank"
              rel="nofollow noopener"
              className="flex items-center justify-center gap-3 bg-emerald-500 hover:bg-emerald-600 text-white py-4 rounded-xl font-semibold font-sora text-sm transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-lg" />
              Falar pelo WhatsApp
            </a>

            <div className="bg-emerald-950 rounded-xl p-5">
              <p className="font-sora font-semibold text-white text-sm mb-2">Resposta em até 1 dia útil</p>
              <p className="font-inter text-emerald-100/60 text-xs leading-relaxed">
                Nossa equipe analisa cada contato com cuidado e responde com uma proposta personalizada para o seu negócio.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
